import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell, { Icon } from './AdminShell';
import DataTable from './DataTable';
import { Modal, Drawer, Summary, Timeline, Tabs, EmptyState } from './Overlay';
import { text } from './peopleCopy';

const gql = (query, variables) => fetch('/graphql', {
  method: 'post', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }), credentials: 'include'
}).then(r => r.json());

const Q = `query ($currentPage: Int, $limit: Int, $searchList: String, $rating: Int) {
  getAdminReviews(currentPage: $currentPage, limit: $limit, searchList: $searchList, rating: $rating) {
    count total average status errorMessage
    results { id bookingId customer provider direction rating comment createdAt }
  }
}`;
const M = `mutation ($reviewId: Int!) {
  removeAdminReview(reviewId: $reviewId) { status errorMessage }
}`;
const Q_BOOKING = `query ($searchList: String) {
  getAllBookings(currentPage: 1, limit: 1, searchList: $searchList) {
    status results { id status totalFare currency createdAt completedAt updatedAt userLocation bookingType
      userDetails { firstName lastName } partnerDetails { firstName lastName } categoryDetails { name } }
  }
}`;
const stamp = value => {
  const d = new Date(Number(value) || value);
  if (isNaN(d)) return '—';
  return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 16)}`;
};
const directionLabel = (direction, locale) => direction === 'provider_to_customer'
  ? text(locale, 'Provider → customer', 'مقدم الخدمة ← العميل') : text(locale, 'Customer → provider', 'العميل ← مقدم الخدمة');
const person = p => p ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : '';

class Ratings extends React.Component {
  state = { rows: [], count: 0, total: 0, average: 0, page: 1, perPage: 10,
    search: '', rating: '', loading: true, error: null, viewing: null, booking: null, tab: 0, removing: null, saving: false, notice: null };

  locale = () => this.props.intl && this.props.intl.locale;
  tx = (en, ar) => text(this.locale(), en, ar);

  componentDidMount() { this.load(); }
  vars = () => ({ currentPage: this.state.page, limit: this.state.perPage,
    searchList: this.state.search || undefined,
    rating: this.state.rating ? Number(this.state.rating) : undefined });
  load = async quiet => {
    if (!quiet) this.setState({ loading: true, error: null });
    const json = await gql(Q, this.vars()).catch(() => null);
    const res = json && json.data && json.data.getAdminReviews;
    if (!res || res.status !== 200) {
      this.setState({ loading: false, error: (res && res.errorMessage) || 'Could not load reviews.' });
      return;
    }
    this.setState({ rows: res.results || [], count: res.count || 0, total: res.total || 0,
      average: res.average || 0, loading: false });
  };
  setPage = page => this.setState({ page }, () => this.load());
  setPerPage = perPage => this.setState({ perPage, page: 1 }, () => this.load());
  setSearch = search => this.setState({ search, page: 1 }, () => this.load());
  openBooking = async review => {
    this.setState({ viewing: review, booking: null, tab: 0, notice: null });
    const json = await gql(Q_BOOKING, { searchList: String(review.bookingId) }).catch(() => null);
    const res = json && json.data && json.data.getAllBookings;
    this.setState({ booking: res && res.results && res.results[0] || null });
  };

  remove = async () => {
    const row = this.state.removing;
    this.setState({ saving: true });
    const json = await gql(M, { reviewId: row.id }).catch(() => null);
    const res = json && json.data && json.data.removeAdminReview;
    this.setState({ saving: false });
    if (!res || res.status !== 200) {
      this.setState({ notice: { tone: 'danger', text: (res && res.errorMessage) || 'Could not remove review.' } });
      return;
    }
    this.setState({ removing: null, notice: { tone: 'danger', text: this.tx(`Review for BK-${row.bookingId} removed.`, `تم حذف مراجعة BK-${row.bookingId}.`) } });
    this.load(true);
  };

  columns = () => [
    { key: 'bookingId', label: this.tx('Booking', 'الحجز'), sortable: true,
      render: r => <span className="mono">BK-{r.bookingId}</span> },
    { key: 'customer', label: this.tx('Customer', 'العميل'), sortable: true },
    { key: 'provider', label: this.tx('Provider', 'مقدم الخدمة'), sortable: true },
    { key: 'direction', label: this.tx('Review', 'المراجعة'), render: r => <span className="tag info">{directionLabel(r.direction, this.locale())}</span> },
    { key: 'rating', label: this.tx('Rating', 'التقييم'), sortable: true,
      render: r => <span className={`tag ${r.rating >= 4 ? 'ok' : r.rating >= 3 ? 'warn' : 'danger'}`}>
        <i />★ {r.rating.toFixed(1).replace('.0', '')}</span> },
    { key: 'comment', label: this.tx('Comment', 'التعليق'), render: r => r.comment
      ? <span title={r.comment} style={{ display: 'inline-block', maxWidth: 360,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>{r.comment}</span>
      : <span className="muted">{this.tx('No comment', 'لا يوجد تعليق')}</span> },
    { key: 'createdAt', label: this.tx('Time', 'الوقت'), align: 'end', sortable: true,
      render: r => <span className="mono">{stamp(r.createdAt)}</span> },
    { key: 'act', label: '', align: 'end', render: r => <span style={{ display: 'inline-flex', gap: 6 }}>
      <button className="btn icon" type="button" title={this.tx('View review', 'عرض المراجعة')} aria-label={`${this.tx('View review', 'عرض المراجعة')} BK-${r.bookingId}`}
        onClick={() => this.openBooking(r)}><Icon name="eye" /></button>
      <button className="btn icon" type="button" title={this.tx('Remove review', 'حذف المراجعة')}
        aria-label={`${this.tx('Remove review for', 'حذف مراجعة')} BK-${r.bookingId}`}
        onClick={() => this.setState({ removing: r, notice: null })}><Icon name="trash" /></button>
    </span> }
  ];

  render() {
    const { rows, count, total, average, page, perPage, search, rating,
      loading, error, viewing, booking, tab, removing, saving, notice } = this.state;
    return <AdminShell current="ratings" breadcrumb={[this.tx('People', 'الأشخاص'), this.tx('Ratings & reviews', 'التقييمات والمراجعات')]}
      title={this.tx('Ratings & reviews', 'التقييمات والمراجعات')} lead={this.tx(`Average ${average.toFixed(2)} across ${total.toLocaleString('en-US')} reviews.`, `المتوسط ${average.toFixed(2)} عبر ${total.toLocaleString('ar-AE')} مراجعة.`)}
      actions={[<a className="btn ghost" href="/siteadmin/ratings" key="classic">{this.tx('Open in classic', 'فتح النسخة الكلاسيكية')}</a>]}>
      {notice && <div className={`notice ${notice.tone}`}><span className="ni">!</span>
        <span className="nt">{notice.text}</span></div>}
      {viewing && <Drawer
        title={`BK-${viewing.bookingId}`}
        sub={booking && [(booking.categoryDetails || {}).name, booking.userLocation, stamp(booking.createdAt).slice(11)].filter(Boolean).join(' · ')}
        headExtra={<span className="tag ok"><i />{booking ? String(booking.status || '').toUpperCase() : this.tx('Loading', 'جارٍ التحميل')}</span>}
        tabs={<Tabs labels={[this.tx('Overview', 'نظرة عامة'), this.tx('Review', 'المراجعة'), this.tx('Chat', 'المحادثة')]}
          active={tab} onChange={i => this.setState({ tab: i })} />}
        onClose={() => this.setState({ viewing: null, booking: null })}
        closeLabel={this.tx('Close', 'إغلاق')}
        actions={<button className="btn ghost" type="button" onClick={() => this.setState({ viewing: null, booking: null })}>{this.tx('Close', 'إغلاق')}</button>}>
        {tab === 0 && (booking ? <div>
          <Summary pairs={[
            [this.tx('Reference', 'المرجع'), <span className="mono">BK-{booking.id}</span>],
            [this.tx('Status', 'الحالة'), <span className="tag ok"><i />{String(booking.status || '').toUpperCase()}</span>],
            [this.tx('Type', 'النوع'), booking.bookingType === 3 ? this.tx('Direct', 'مباشر') : this.tx('On-demand', 'عند الطلب')],
            [this.tx('Customer', 'العميل'), person(booking.userDetails) || viewing.customer],
            [this.tx('Provider', 'مقدم الخدمة'), person(booking.partnerDetails) || viewing.provider],
            [this.tx('Service', 'الخدمة'), (booking.categoryDetails || {}).name || '—'],
            [this.tx('Area', 'المنطقة'), booking.userLocation || '—'],
            [this.tx('Fare', 'الأجرة'), `${booking.currency || 'AED'} ${Math.round(Number(booking.totalFare) || 0)}`]
          ]} />
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--tx-3)', letterSpacing: '.11em', textTransform: 'uppercase', fontWeight: 700, margin: '18px 0 12px' }}>
            {this.tx('Lifecycle', 'دورة الحياة')}
          </div>
          <Timeline events={[
            { title: this.tx('Booking created', 'تم إنشاء الحجز'), detail: `${person(booking.userDetails) || viewing.customer} · ${(booking.categoryDetails || {}).name || '—'}`, at: stamp(booking.createdAt).slice(11), color: 'var(--info)' },
            ...(person(booking.partnerDetails) ? [{ title: this.tx('Provider assigned', 'تم تعيين مقدم الخدمة'), detail: person(booking.partnerDetails), at: stamp(booking.createdAt).slice(11), color: 'var(--brand-amber)' }] : []),
            ...(booking.status === 'completed' ? [{ title: this.tx('Completed & settled', 'اكتمل وتمت التسوية'), detail: `${booking.currency || 'AED'} ${Math.round(Number(booking.totalFare) || 0)}`, at: stamp(booking.completedAt || booking.updatedAt || booking.createdAt).slice(11), color: 'var(--ok)' }] : [])
          ]} />
        </div> : <p style={{ color: 'var(--tx-3)' }}>{this.tx('Loading booking…', 'جارٍ تحميل الحجز…')}</p>)}
        {tab === 1 && <Summary pairs={[
          [this.tx('Direction', 'الاتجاه'), directionLabel(viewing.direction, this.locale())],
          [this.tx('Rating', 'التقييم'), `★ ${viewing.rating}`],
          [this.tx('Comment', 'التعليق'), viewing.comment || this.tx('No comment', 'لا يوجد تعليق')],
          [this.tx('Time', 'الوقت'), stamp(viewing.createdAt)]
        ]} />}
        {tab === 2 && <EmptyState icon="chat" title={this.tx('No messages', 'لا توجد رسائل')} msg={this.tx('No messages are recorded for this booking.', 'لا توجد رسائل مسجلة لهذا الحجز.')} />}
      </Drawer>}
      {removing && <Modal icon="trash" tone="danger"
        title={`${this.tx('Remove review for', 'حذف مراجعة')} BK-${removing.bookingId}?`}
        sub={this.tx("Removing a review changes the reviewee's average rating. Only do this for abuse or personal data.", 'حذف المراجعة يغيّر متوسط تقييم الشخص المُقيّم. استخدم ذلك فقط للإساءة أو البيانات الشخصية.')}
        confirmLabel={this.tx('Remove review', 'حذف المراجعة')} busy={saving} onConfirm={this.remove}
        onClose={() => this.setState({ removing: null })}>
        <Summary pairs={[
          [this.tx('Customer', 'العميل'), removing.customer], [this.tx('Provider', 'مقدم الخدمة'), removing.provider],
          [this.tx('Rating', 'التقييم'), `★ ${removing.rating}`], [this.tx('Comment', 'التعليق'), removing.comment || this.tx('No comment', 'لا يوجد تعليق')]
        ]} />
      </Modal>}
      {error ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
        : <DataTable columns={this.columns()} rows={rows} count={count} page={page} perPage={perPage}
            loading={loading} onPage={this.setPage} onPerPage={this.setPerPage}
            onSearch={this.setSearch} searchValue={search}
            searchPlaceholder={this.tx('Search reference, name or comment', 'ابحث بالمرجع أو الاسم أو التعليق')}
            filters={<select className="inp" value={rating} aria-label={this.tx('Filter by stars', 'تصفية حسب النجوم')}
              onChange={e => this.setState({ rating: e.target.value, page: 1 }, () => this.load())}>
              <option value="">{this.tx('Stars: all', 'النجوم: الكل')}</option>
              {[5, 4, 3, 2, 1].map(n => <option value={n} key={n}>{n} {this.tx(n === 1 ? 'star' : 'stars', n === 1 ? 'نجمة' : 'نجوم')}</option>)}
            </select>} emptyText={this.tx('No reviews yet', 'لا توجد مراجعات بعد')} emptyHint={this.tx('Rated jobs will appear here.', 'ستظهر الوظائف التي حصلت على تقييم هنا.')} />}
    </AdminShell>;
  }
}

export default injectIntl(Ratings);
